<template>
  <div class="bill-form-container">
    <div class="section-container">
      <substrate-mount>
        <div class="composite-textarea">
          <input-textarea
            ref="billDescription"
            v-model="bill.description"
            input-id="bill-description"
            :input-name="$t('common.description')"
            :input-title="$t('enter.description')"
            :input-placeholder="$t('what.paid')"
            :max-length="510"
            :required="false"
            :rows="1"
            composite
          />
          <attachment-image
            :link="bill.attachment.link"
            input-id="bill.attachment"
            type="secondary"
            composite
            composite-textarea
            @loading="onImageLoading"
            @uploaded="data => bill.attachment.setData(data)"
            @reset="bill.attachment.reset()"
          />
        </div>
        <labeled-input
          v-model="bill.amount"
          :label="$t('amount.for.pay', { getCurrencySign: currency.sign })"
          :placeholder="`0 ${currency.sign}`"
          :error="$v.bill.amount.$error"
          :force-replace="[
            [/[^0-9\.\,]+/g, ''],
            [/^\./g, ''],
            [/\,/, '\.'],
          ]"
          :force-format="[$formatInputAmount({
            significantZeros: currency.significant_zeros,
            decimalsSeparator: '.',
            thousandSeparator: ' ',
          })]"
          @focus="$v.bill.amount.$reset"
          @blur="$v.bill.amount.$touch"
        />
        <div class="switcher-container">
          <cs-switcher
            v-model="bill.isMulti"
            :label="$t('allow.multiple.bill.payments')"
          />
        </div>
        <div class="switcher-container">
          <cs-switcher
            v-model="bill.isContentUrl"
            :label="$t('organize.content.delivery')"
            @input="bill.contentUrl = ''"
          />
        </div>
        <div v-if="bill.isContentUrl" class="input-container">
          <input-link-or-iframe
            ref="billContentUrl"
            v-model="bill.contentUrl"
            input-id="bill-content-url"
            :input-name="$t('link.to.content.or.code.for.embedding')"
            input-placeholder="https://mysite.ru/myfile.pdf"
            capusta-info="billIframe"
            @showInfo="$emit('showInfo')"
          />
        </div>
      </substrate-mount>
    </div>

    <div class="button-primary-container">
      <cs-button :disabled="!isValid" @click="onSubmit">
        {{ $t('get.link') }}
      </cs-button>
    </div>

    <div class="button-secondary-container">
      <cs-button type="secondary" :disabled="!isValid" @click="onSubmitWithQrCode">
        <span class="icon">
          <cs-icon name="qr-code" size="extra-medium" />
        </span>
        {{ $t('get.qr') }}
      </cs-button>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import { formatAmount } from 'lib/utils/amount';

import attachmentMixin from 'lib/mixins/attachmentMixin';

import { isLessMaxAmount, isMoreMinAmount } from '@/components/inputElements/CustomValidators';

import SubstrateMount from '@/components/capusta/SubstrateMount';
import InputTextarea from '@/components/inputElements/InputTextarea';
import InputLinkOrIframe from '@/components/inputElements/InputLinkOrIframe';

import AttachmentImage from 'modules/AttachmentImage';

import LabeledInput from 'molecules/common/LabeledInput';

import Attachment from 'lib/project/classes/Attachment';
import BillForm from '../classes/BillForm';

export default {
  name: 'BillInvoicing',
  mixins: [
    attachmentMixin,
  ],
  components: {
    SubstrateMount,
    InputTextarea,
    LabeledInput,
    InputLinkOrIframe,
    AttachmentImage,
  },
  props: {
    transaction: Object,
  },
  mounted() {
    this.fillingData();
  },
  data() {
    return {
      bill: new BillForm,
    };
  },
  validations() {
    return {
      bill: {
        amount: {
          minValue: isMoreMinAmount(this.limits.min_amount, this.currency),
          maxValue: isLessMaxAmount(this.limits.max_amount, 0, this.currency),
        },
      },
    };
  },
  computed: {
    currency() {
      return this.PROJECT_INFO.currency;
    },
    formattedAmount() {
      const replacedAmount = this.bill.amount.replace(/\s/g, '');
      const preparedAmount = Math.round(replacedAmount * 10 ** this.currency.significant_zeros);

      return this.$validate.isPositiveNumber(preparedAmount) ? preparedAmount : null;
    },
    contentUrl() {
      if (!this.bill.isContentUrl) {
        return null;
      }

      const { contentUrl } = this.bill;
      const checkProtocol = contentUrl.match(/^http[s]?:\/\//);
      const checkOnIframeTag = /^<iframe.+<\/iframe>$/.test(contentUrl);

      if (checkOnIframeTag) {
        return contentUrl;
      }

      if (this.$validate.isNull(checkProtocol)) {
        return `https://${contentUrl}`;
      }

      return contentUrl;
    },
    isValid() {
      if (this.submittingDisabled) {
        return false;
      }

      if (this.bill.isContentUrl) {
        return this.isValidContentUrl && this.isValidAmount;
      }

      return this.isValidAmount;
    },
    isValidAmount() {
      if (this.$validate.isEmptyString(this.bill.amount)) {
        return true;
      }

      return !this.$v.bill.$invalid;
    },
    isValidContentUrl() {
      if (this.$validate.isEmptyString(this.bill.contentUrl)) {
        return false;
      }

      return this.$validate.isNotNull(this.contentUrl) && this.$refs.billContentUrl && !this.$refs.billContentUrl.$v.$error;
    },
    ...mapState(['PROJECT_INFO']),
    ...mapGetters('bills', ['limits']),
  },
  methods: {
    onSubmitWithQrCode() {
      this.onSubmit(true);
    },
    onSubmit(useQrCode = false) {
      if (!this.isValid) {
        this.$alert({
          type: 'warning',
          text: 'mistakes.in.form',
        });
        return;
      }

      const tariff = {
        name: null,
        description: null,
        subscription: null,
        contentUrl: this.contentUrl,
        amount: this.formattedAmount,
        currency: this.currency,
      };

      const submittedData = {
        billTariffs: [tariff],
        multiBill: this.bill.isMulti,
        description: this.bill.description || null,
        attachment: this.bill.attachment.getData(),
        type: this.bill.type,
      };

      this.$emit('submit', {
        bill: submittedData,
        qrCode: useQrCode,
      });
    },
    async fillingData() {
      if (!this.transaction) {
        this.bill.currency = this.currency;
        return false;
      }

      this.bill.description = this.transaction.description || null;
      this.bill.isMulti = this.transaction.multiBill;

      this.bill.amount = this.getBillAmount(this.transaction);
      this.bill.attachment = new Attachment(this.transaction.attachment);

      this.$nextTick(async () => {
        this.bill.isContentUrl = this.transaction.billTariffs
          .some(tariff => this.$validate.isNotNull(tariff.contentUrl));

        this.bill.contentUrl = await this.transaction.billTariffs[0].contentUrl;
      });
    },
    getBillAmount(data) {
      const { billTariffs } = data;

      if (this.$validate.isUndefined(billTariffs)) {
        return '';
      }

      if (this.$validate.isNull(billTariffs[0].amount)) {
        return '';
      }

      return formatAmount(billTariffs[0].amount, {
        significant_zeros: this.currency.significant_zeros,
        type: this.currency.type,
      });
    },
  },
};
</script>

<style lang="sass" scoped>
  .section-container
    margin-bottom: 20px

  .input-container
    &:not(:last-child)
      margin-bottom: 20px

    .input,
    .textarea
      margin: 0

  .composite-textarea
    position: relative
    display: flex
    align-items: center

    .textarea
      margin: 0

  .switcher-container
    &:not(:last-child)
      margin-bottom: 20px

  .button-primary-container
    display: flex
    justify-content: center
    margin-bottom: 20px

  .button-secondary-container
    display: flex
    justify-content: center

    & .icon
      margin-right: 10px
</style>
